import React, { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

const KF_ID = '__cbconnect-kf__';

function ensureKeyframes() {
  if (typeof document === 'undefined' || document.getElementById(KF_ID)) return;
  const s = document.createElement('style');
  s.id = KF_ID;
  s.textContent = `
    @keyframes cbSpin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes cbPulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.72; }
    }
  `;
  document.head.appendChild(s);
}

// Compass-ring tick marks - generated from the original SVG geometry
function renderTicks(color) {
  const lines = [];
  const cx = 100, cy = 100, rOuter = 86;
  for (let deg = 0; deg < 360; deg += 10) {
    const a = (deg * Math.PI) / 180;
    const isMajor = deg % 30 === 0;
    const rInner = isMajor ? 78 : 82;
    lines.push(
      <line
        key={deg}
        x1={cx + rOuter * Math.cos(a)}
        y1={cy + rOuter * Math.sin(a)}
        x2={cx + rInner * Math.cos(a)}
        y2={cy + rInner * Math.sin(a)}
        stroke={color}
        strokeWidth={isMajor ? 2 : 1}
        opacity="0.7"
      />
    );
  }
  return lines;
}

/**
 * CBConnectLogo
 *
 * Props:
 *   size        - pixel dimensions (default 40)
 *   animated    - spin + pulse the logo for a loading state (default false)
 *   showLabel   - show arc text "CARTE · BRUNE · CEDEAO" (default true)
 *   embleme     - image src rendered inside the disc, in place of the "CB"
 *                 logotype (used on the login hero, with the Carte Brune badge)
 *   cadre       - draw the compass ring + outer circle (default true). Set to
 *                 false in cramped spots: at 36-56px the frame eats the box and
 *                 leaves the emblem a quarter of it, unreadable.
 *   variant     - 'color' | 'white' | 'dark'  (default 'color')
 *   speed       - 'slow' | 'normal' | 'fast'  (default 'normal', only when animated)
 *   className / style - forwarded to the <svg>
 */
export default function CBConnectLogo({
  size = 40,
  animated = false,
  showLabel = true,
  embleme,
  cadre = true,
  variant = 'color',
  speed = 'normal',
  className,
  style,
}) {
  const { t } = useTranslation('commun');
  useEffect(() => {
    if (animated) ensureKeyframes();
  }, [animated]);

  const uid = useId().replace(/:/g, '');
  const gradId = `cbg-${uid}`;
  const arcId = `cba-${uid}`;
  const clipId = `cbc-${uid}`;

  /**
   * Rayon du coeur. Avec le cadre, le disque est cantonne a l'interieur de
   * l'anneau (r = 46 sur 100, soit un quart de la surface). Sans cadre, le
   * badge occupe toute la boite : a 56px dans la barre laterale, cela le fait
   * passer de 26px a 56px - la difference entre une tache et un sceau lisible.
   */
  const rCoeur = cadre ? 46 : 98;

  const spinDuration = { slow: '4s', normal: '2.4s', fast: '1.2s' }[speed] ?? '2.4s';

  const palette = {
    color: {
      useGrad: true,
      text: '#0D1117',
      ring: '#F0F6FC',
      ticks: '#8B949E',
      arc: '#8B949E',
    },
    white: {
      useGrad: false,
      inner: 'rgba(255,255,255,0.18)',
      text: '#ffffff',
      ring: 'rgba(255,255,255,0.65)',
      ticks: 'rgba(255,255,255,0.55)',
      arc: 'rgba(255,255,255,0.50)',
    },
    dark: {
      useGrad: true,
      text: '#F0F6FC',
      ring: '#21262D',
      ticks: '#8B949E',
      arc: '#8B949E',
    },
  }[variant] ?? {};

  const innerFill = palette.useGrad ? `url(#${gradId})` : (palette.inner ?? '#2EA44F');

  const spinStyle = animated
    ? { animation: `cbSpin ${spinDuration} linear infinite`, transformOrigin: '100px 100px' }
    : undefined;

  const pulseStyle = animated
    ? { animation: 'cbPulse 1.8s ease-in-out infinite' }
    : undefined;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      width={size}
      height={size}
      aria-label="CBConnect"
      role="img"
      className={className}
      style={style}
    >
      <defs>
        {palette.useGrad && (
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#34D058" />
            <stop offset="0.55" stopColor="#2EA44F" />
            <stop offset="1" stopColor="#1A7A38" />
          </linearGradient>
        )}
        {showLabel && <path id={arcId} d="M 30 100 A 70 70 0 0 1 170 100" />}
        {/* Le badge Carte Brune est une image carree a coins BLANCS (pas de
            canal alpha utile) : sans decoupe circulaire, il poserait un carre
            blanc sur le fond vert sombre. */}
        {embleme && (
          <clipPath id={clipId}>
            <circle cx="100" cy="100" r={rCoeur} />
          </clipPath>
        )}
      </defs>

      {cadre && (
        <>
          {/* Compass-ring - rotates in loader mode */}
          <g style={spinStyle}>
            {renderTicks(palette.ticks)}
          </g>

          {/* Outer circle */}
          <circle cx="100" cy="100" r="64" fill="none" stroke={palette.ring} strokeWidth="2" />
        </>
      )}

      {/* Coeur du logo : le badge Carte Brune s'il est fourni, sinon le
          logotype « CB » sur son disque degrade. */}
      {embleme ? (
        <g style={pulseStyle}>
          {/* Liseré blanc d'un point : rattrape l'anticrenelage de la decoupe
              et detache le badge du fond, au lieu d'un bord vert sale. */}
          <circle cx="100" cy="100" r={rCoeur + 1} fill="#ffffff" />
          <image
            href={embleme}
            x={100 - rCoeur} y={100 - rCoeur}
            width={rCoeur * 2} height={rCoeur * 2}
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${clipId})`}
          />
        </g>
      ) : (
        <>
          {/* Inner gradient disc - pulses in loader mode */}
          <circle cx="100" cy="100" r="46" fill={innerFill} style={pulseStyle} />

          {/* Logotype */}
          <text
            x="100" y="104"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="Syne, sans-serif"
            fontWeight="800"
            fontSize="32"
            letterSpacing="-1.5"
            fill={palette.text}
          >
            CB
          </text>
        </>
      )}

      {/* Arc tagline */}
      {showLabel && (
        <text fontFamily="DM Mono, monospace" fontSize="9" letterSpacing="3" fill={palette.arc}>
          <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
            {t('cbConnectLogo.devise')}
          </textPath>
        </text>
      )}
    </svg>
  );
}

/**
 * CBConnectLoader - branded fullscreen / inline loading overlay
 *
 * Props:
 *   fullscreen - fixed overlay covering the whole viewport (default false)
 *   text       - optional status text below the logo
 *   size       - logo size in px (default 80)
 *   speed      - spinner speed forwarded to CBConnectLogo
 */
export function CBConnectLoader({ fullscreen = false, text, size = 80, speed = 'normal' }) {
  const wrapStyle = fullscreen
    ? {
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(16, 38, 25, 0.93)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
    }
    : {
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px',
    };

  return (
    <div style={wrapStyle} aria-busy="true" aria-live="polite">
      <CBConnectLogo
        size={size}
        animated
        showLabel={false}
        variant="white"
        speed={speed}
      />
      {text && (
        <p style={{
          marginTop: 18,
          color: 'rgba(255,255,255,0.65)',
          fontSize: 13,
          fontFamily: 'inherit',
          letterSpacing: '0.4px',
          textAlign: 'center',
        }}>
          {text}
        </p>
      )}
    </div>
  );
}
